/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useInlineDisplayable } from "@app/resources/hooks/use-inline-displayable";
import { useResourceIcon } from "@app/resources/hooks/use-resource-icon";
import { useResourceIconStyling } from "@app/resources/hooks/use-resource-icon-styling";
import { useResourceLabel } from "@app/resources/hooks/use-resource-label";
import { useResourceTagStyling } from "@app/resources/hooks/use-resource-tag-styling";
import { useResourceTheme } from "@app/resources/hooks/use-resource-theme";
import { useTrackResourceView } from "@core/activities/hooks";
import { type Resource } from "@core/shared/types";
import { useMemo, useState } from "react";

export function useResourceItem(resource: Resource) {
  const {
    linkedResource,
    linkedResources,
    isLocked,
    type,
    url,
    title,
    image,
    duration,
  } = resource;

  const [
    { trackResourceView },
    { getResourceIcon },
    { getResourceIconStyle },
    { getResourceTheme },
    { getResourceTypeTagStyle },
    { getResourceTypeLabel },
    { isInlineDisplayable },
  ] = [
    useTrackResourceView(resource),
    useResourceIcon(),
    useResourceIconStyling(),
    useResourceTheme(),
    useResourceTagStyling(),
    useResourceLabel(),
    useInlineDisplayable(),
  ];

  const [isExpanded, setIsExpanded] = useState(false);

  const allLinkedResources = useMemo(() => {
    const resources: Resource[] = [];

    // Add single linked resource if exists
    if (linkedResource) {
      resources.push(linkedResource as Resource);
    }

    // Add multiple linked resources if exists
    if (linkedResources?.length) {
      resources.push(...linkedResources);
    }

    return resources;
  }, [linkedResource, linkedResources]);

  const handleCardClick = () => {
    if (isInlineDisplayable(type)) {
      setIsExpanded(!isExpanded);
    } else if (!isLocked) {
      const link = document.createElement("a");
      link.href = url;
      link.download = title || `${getResourceTypeLabel(type)} File`;
      link.click();
      trackResourceView();
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLocked) return;

    if (isInlineDisplayable(type)) {
      setIsExpanded(!isExpanded);
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = title || `${getResourceTypeLabel(type)} File`;
      link.click();
      trackResourceView();
    }
  };

  const theme = getResourceTheme(type);

  const hasImage = image && image.url;

  const handleClick = () => {
    if (isInlineDisplayable(type)) {
      // Always allow toggling for inline displayable resources
      setIsExpanded(!isExpanded);
    } else if (!isLocked) {
      // Only allow download for non-locked resources
      const link = document.createElement("a");
      link.href = url;
      link.download = title || `${getResourceTypeLabel(type)} File`;
      link.click();
    }
  };

  return {
    handleCardClick,
    handleActionClick,
    isExpanded,
    allLinkedResources,
    theme,
    hasImage,
    title,
    type,
    duration,
    isLocked,
    image,
    getResourceIcon,
    getResourceIconStyle,
    getResourceTypeTagStyle,
    getResourceTheme,
    getResourceTypeLabel,
    isInlineDisplayable,
    linkedResource,
    linkedResources,
    url,
    trackResourceView,
    handleClick,
  };
}
